import { z } from "zod";
import { ActorId, CountryCode } from "./primitives";

export const ActorKind = z.enum(["guest", "resident", "staff"]);
export type ActorKind = z.infer<typeof ActorKind>;

/**
 * Kinds of the actors a room shows: the account kinds above plus "npc", the AI residents of ADR-0010.
 * Deliberately NOT part of ActorKind: an AI resident has no account, no PublicActor, never reaches the
 * API, and the presence / analytics / statistics inputs are typed with the account kinds only, so an
 * "npc" can never be passed to them by mistake (it would not compile).
 */
export const RoomActorKind = z.enum(["guest", "resident", "staff", "npc"]);
export type RoomActorKind = z.infer<typeof RoomActorKind>;

/**
 * Public actor id prefix of AI residents (ADR-0010). Visitor ids are `act_` + 26 base32 characters
 * (`a-z2-7`, never a "0"), so `act_00ai...` can never be a real visitor's id and the AI badge can be
 * derived from the id alone (renderer name tag, chat panel) without trusting anything else. AI
 * resident ids still match the ActorId format so their chat lines pass the ChatMessage schema.
 */
export const AI_RESIDENT_ACTOR_ID_PREFIX = "act_00ai";

/** True for an AI resident's public actor id (`act_00ai` + 16..28 lowercase letters/digits). */
export function isAiResidentActorId(actorId: string): boolean {
  return /^act_00ai[a-z0-9]{16,28}$/.test(actorId);
}

/**
 * First names the AI residents use (ADR-0010 decision 4). Each passes the language filter's name
 * check and the display-name format. They are reserved: account display names equal to one of them
 * (case-insensitive) must be refused (`isAiResidentName`), so no visitor can pass as a resident or
 * be mistaken for one.
 */
export const AI_RESIDENT_NAMES = [
  { name: "Maya", bodyType: "female" },
  { name: "Theo", bodyType: "male" },
  { name: "Nora", bodyType: "female" },
  { name: "Omar", bodyType: "male" },
  { name: "Priya", bodyType: "female" },
  { name: "Felix", bodyType: "male" },
  { name: "Lena", bodyType: "female" },
  { name: "Mateo", bodyType: "male" },
  { name: "Hana", bodyType: "female" },
  { name: "Kofi", bodyType: "male" },
  { name: "Clara", bodyType: "female" },
  { name: "Jonas", bodyType: "male" },
  { name: "Rosa", bodyType: "female" },
  { name: "Elliot", bodyType: "male" },
  { name: "Zara", bodyType: "female" },
  { name: "Ruben", bodyType: "male" },
  { name: "Iris", bodyType: "female" },
  { name: "Malik", bodyType: "male" },
  { name: "June", bodyType: "female" },
  { name: "Hugo", bodyType: "male" },
] as const satisfies readonly { name: string; bodyType: (typeof BODY_TYPES)[number] }[];

/** The plain refusal when someone picks an AI resident's name (founder 2026-10-21: a clear message, not a silent swap). */
export const AI_RESIDENT_NAME_REFUSED_REASON = "That name belongs to one of Townsy's AI residents. Please choose a different name.";

/** True when `name` is (case-insensitively) one of the AI residents' names: reserved for display names. */
export function isAiResidentName(name: string): boolean {
  const n = name.trim().toLowerCase();
  return AI_RESIDENT_NAMES.some((r) => r.name.toLowerCase() === n);
}

/**
 * Approved appearance options. The server stores and validates these; clients may only
 * reference ids from the catalog below (no arbitrary URLs, meshes, or colors).
 *
 * - bodyType: chosen by the resident (founder request 2026-10-09: "male/female"); never inferred
 *   from names or country (docs/03 section 6). Guests get a random one.
 * - bodyColor: the SHIRT color (kept under its original name for stored-data compatibility).
 * - skinTone / hairStyle / hairColor / outfit: approved swatches only.
 *
 * Fields added after v1 carry defaults so older stored appearances still parse.
 */
export const BODY_TYPES = ["male", "female"] as const;
export const BODY_COLORS = ["cobalt", "coral", "moss", "amber", "plum", "teal", "slate", "rose", "white", "black"] as const;
export const SKIN_TONES = ["s1", "s2", "s3", "s4", "s5", "s6"] as const;
export const HAIR_STYLES = ["short", "long", "curly", "bun", "cap", "none"] as const;
export const HAIR_COLORS = ["black", "brown", "auburn", "blonde", "red", "grey", "blue", "pink"] as const;
export const OUTFITS = ["casual", "hoodie", "suit", "dress", "sport"] as const;

/** Display labels for the customization UI (no meaning beyond presentation). */
export const APPEARANCE_LABELS = {
  bodyType: { male: "Male", female: "Female" },
  bodyColor: {
    cobalt: "Cobalt",
    coral: "Coral",
    moss: "Moss",
    amber: "Amber",
    plum: "Plum",
    teal: "Teal",
    slate: "Slate",
    rose: "Rose",
    white: "White",
    black: "Black",
  },
  skinTone: { s1: "Tone 1", s2: "Tone 2", s3: "Tone 3", s4: "Tone 4", s5: "Tone 5", s6: "Tone 6" },
  hairStyle: { short: "Short", long: "Long", curly: "Curly", bun: "Bun", cap: "Cap", none: "None" },
  hairColor: {
    black: "Black",
    brown: "Brown",
    auburn: "Auburn",
    blonde: "Blonde",
    red: "Red",
    grey: "Grey",
    blue: "Blue",
    pink: "Pink",
  },
  outfit: { casual: "Casual", hoodie: "Hoodie", suit: "Suit", dress: "Dress", sport: "Sport" },
} as const;

export const Appearance = z.object({
  bodyType: z.enum(BODY_TYPES).default("male"),
  bodyColor: z.enum(BODY_COLORS),
  skinTone: z.enum(SKIN_TONES),
  hairStyle: z.enum(HAIR_STYLES),
  hairColor: z.enum(HAIR_COLORS).default("brown"),
  outfit: z.enum(OUTFITS),
  version: z.number().int().min(1),
});
export type Appearance = z.infer<typeof Appearance>;

/** What a resident may submit (version is assigned by the server). */
export const AppearanceChoice = Appearance.omit({ version: true }).strict();
export type AppearanceChoice = z.infer<typeof AppearanceChoice>;

/** What everyone in a room may see about an actor. No auth ids, email, balances, or roles. */
export const PublicActor = z.object({
  actorId: ActorId,
  kind: ActorKind,
  label: z.string().min(1).max(40),
  countryCode: CountryCode.nullable(),
  appearance: Appearance,
});
export type PublicActor = z.infer<typeof PublicActor>;

export const GUEST_LABEL_PREFIX = "Guest";
export const GUEST_NUMBER_DIGITS = 7;
export const GuestLabel = z.string().regex(/^Guest\d{7}$/);
